import dayjs from 'dayjs';

/**
 * 문자열에서 숫자만 추출하는 함수
 * @param {string} value - 변환하고 싶은 문자열
 * @return {string} - 숫자만 추출된 문자열
 */
export const numbersOnly = (value: string): string =>
  !value
    ? ''
    : value
        .split('')
        .filter((v) => !isNaN(Number(v)))
        .join('');

/**
 * 숫자나 문자열을 받아 local String(en-US) 기준으로 변환하여 반환하는 함수
 * @param {string} value - 변환하고 싶은 문자열
 * @return {string} - local(en-US)기준으로 변환된 문자열
 */
export const currencyCommas = (value: number | string): string =>
  isNaN(Number(value)) ? String(value) : value.toLocaleString('en-US');

/**
 * 문자열이 숫자로만 구성되어 있는지 검사하는 함수
 * @param {string} value - 검사하고 싶은 문자열
 * @return {boolean} - 숫자로만 구성되어 있으면 true, 그 외에는 false
 */
export const isNumberOnlyString = (value: string): boolean =>
  !value ? false : value.length === numbersOnly(value).length;

/**
 * 문자열을 yyyyMMdd 형태로 받아 유효한 날짜인지 검사하는 함수
 * @param {string} yyyyMmDd - 검사하고 싶은 문자열
 * @return {boolean} - 유효한 날짜 형식이면 true, 그 외에는 false
 */
export const isValidYyyyMmDd = (yyyyMmDd: string): boolean => {
  if (yyyyMmDd.length !== 8) {
    return false;
  }

  if (!isNumberOnlyString(yyyyMmDd)) {
    return false;
  }

  const year = Number(yyyyMmDd.substring(0, 4));
  const month = Number(yyyyMmDd.substring(4, 6));
  const day = Number(yyyyMmDd.substring(6));

  if (month < 1 || month > 12) {
    return false;
  }

  const isLeapYear = year % 4 === 0;

  const lastDayOfMonth = [
    0,
    31,
    isLeapYear ? 29 : 28,
    31,
    30,
    31,
    30,
    31,
    31,
    30,
    31,
    30,
    31,
  ];

  return lastDayOfMonth[month] >= day;
};

/**
 * 현재 날짜 기준으로 `n {단위} 전`로 변환하는 함수
 * @param {string} date - 변환하고 싶은 날짜 문자열
 * @return {string} - `n {단위} 전` 형식으로 변환된 문자열
 */
export const fromNowDate = (date: string): string => dayjs(date).fromNow();

/**
 * 문자열을 yyyy[delimiter]MM[delimiter]DD 형태로 변경하는 함수
 * @param {string} date - 변경하고 싶은 문자열(yyyyMMdd 형식)
 * @param {string} delimiter - 구분자(예: -, / 등)
 * @return {string} - yyyy[delimiter]MM[delimiter]DD 형식으로 변환된 문자열
 */
export const yyyyMmDdWithDelimiter = (date: string, delimiter: string) =>
  !date ? '' : dayjs(date).format(`YYYY${delimiter}MM${delimiter}DD`);

/**
 * 날짜 데이터 형식을 yy.mm.dd로 변경하는 함수
 * @param {string} date - 변환하고 싶은 날짜 문자열
 * @return {string} - yy.mm.dd 형식으로 변환된 문자열
 */
export const yyMmDdWithDelimiter = (date: string): string =>
  dayjs(date).format('YY.MM.DD');

/**
 * HTML 태그를 제거하고 텍스트만 추출하는 함수
 * @param {string} htmlString - HTML 문자열
 * @return {string} - HTML 태그를 제거한 텍스트만 추출된 문자열
 */
export const extractTextFromHtml = (htmlString: string): string => {
  if (!htmlString) {
    return '';
  }

  const parsedAsHtml = new DOMParser().parseFromString(htmlString, 'text/html');
  const body = parsedAsHtml.querySelector('body');

  return !body ? '' : body.innerText.replaceAll('\n', ' ');
};

/**
 * 객체를 받아 쿼리스트링으로 변환하는 함수
 * @param {Object} payload - 쿼리스트링으로 변환하고 싶은 객체
 * @return {string} - 쿼리스트링으로 변환된 문자열
 */
export const getQueryString = (payload: { [T in string]: any }) =>
  Object.keys(payload)
    .filter((key) => !!payload[key])
    .map((key) => `${key}=${payload[key]}`)
    .join('&');

/**
 * 주어진 문자열이 API 엔드포인트인지 검사하여 '/'로 시작하고 끝나도록 정규화하는 함수
 * @param {string} endpoint - API 엔드포인트 문자열
 * @return {string} - '/'로 시작하고 끝나는 API 엔드포인트 문자열
 */
export const getValidApiEndpoint = (endpoint: string) =>
  ('/' + endpoint?.split('?')[0]).replace('//', '/'); // /로 시작하고 파라미터 제거

/**
 * 줄바꿈(\\n || \n)문자를 <br> 태그로 변경하는 함수
 * @param {string} content - 줄바꿈(\\n || \n)문자가 포함된 문자열
 * @return {string} - 줄바꿈(\\n || \n)문자를 <br> 태그로 변경한 문자열
 */
export const enterToBr = (content: string) =>
  content.replace('\\n', '<br>').replace('\n', '<br>');

/**
 * 개수 label 노출 함수
 * @param {number} count - 수
 * @return {string} - 0개 '' || 1-99개 '{n}' || 99개 초과 '99+'
 */
export const getCountLabel = (count: number) => {
  if (count === 0) {
    return '';
  }

  if (count > 99) {
    return '99+';
  }

  return count;
};

/**
 * 문자열을 주어진 최대 길이로 자르고, 필요한 경우 말줄임 기호(...)를 추가합니다.
 *
 * @param {string} str - 입력 문자열
 * @param {number} [maxLength=12] - 문자열의 최대 길이
 * @return {string} - 최대 길이에 맞춰 잘린 문자열 또는 원래 문자열
 */
export const truncateString = (
  str: string = '',
  maxLength: number = 12,
): string => {
  if (str.length > maxLength) {
    return str.slice(0, maxLength - 3) + '...';
  }

  return str;
};

/**
 * 주어진 값이 0 이상인지(음수가 아닌지) 확인합니다.
 *
 * @param {number | undefined} value - 확인할 값.
 * @return {boolean} 값이 0 이상이면 true를 반환하고, 그렇지 않으면 false를 반환합니다.
 */
export const isNonNegativeNumber = (value: number | undefined): boolean => {
  if (value === undefined) {
    return false;
  }

  return value >= 0;
};
